import React, { useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Upload,
  Button,
  Space,
  Typography,
  Input,
  ColorPicker,
  Form,
  Divider,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import {
  UploadOutlined,
  SaveOutlined,
  BgColorsOutlined,
  GlobalOutlined,
  MailOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

const WhiteLabelBranding = () => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  const emailTemplates = useMemo(() => ([
    { key: 'welcome', name: 'Welcome Email', description: 'Sent to new users after signup', status: 'Active' },
    { key: 'invite', name: 'Client Invite', description: 'Invite clients to the portal', status: 'Active' },
    { key: 'reset', name: 'Password Reset', description: 'Password reset instructions', status: 'Active' },
    { key: 'invoice', name: 'Invoice', description: 'Billing and invoice emails', status: 'Draft' },
  ]), []);

  const columns = [
    { title: 'Template', dataIndex: 'name', key: 'name' },
    { title: 'Description', dataIndex: 'description', key: 'description', responsive: ['md'] },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 120, render: (s) => (
      <Tag color={s === 'Active' ? 'green' : 'default'}>{s}</Tag>
    ) },
    { title: 'Actions', key: 'actions', width: 180, render: (_, r) => (
      <Space>
        <Button type="link">Preview</Button>
        <Button type="link">Edit</Button>
      </Space>
    ) },
  ];

  const handleUpload = ({ file }) => {
    // Mock upload success
    const url = URL.createObjectURL(file);
    setLogoUrl(url);
    message.success('Logo uploaded');
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      // TODO: Persist to backend e.g., POST /api/whitelabel/branding
      // values: { brandName, primaryColor, secondaryColor, domain, customEmailFrom, whiteLabelEmails }
      void values;
      message.success('Branding settings saved');
    } catch (e) {
      if (e?.errorFields) return;
      message.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>WhiteLabel Branding</Title>
          <Text type="secondary">Configure logos, colors, domains, and email templates for your white-label service.</Text>
        </div>

        {/* Branding form */}
        <Form form={form} layout="vertical" initialValues={{
          brandName: 'Your Brand',
          domain: 'agency.yourdomain.com',
          customEmailFrom: 'noreply@yourdomain.com',
          primaryColor: '#1677ff',
          secondaryColor: '#13c2c2',
          whiteLabelEmails: true,
        }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card title="Brand Basics" extra={<BgColorsOutlined />}>
                <Form.Item label="Brand Name" name="brandName" rules={[{ required: true, message: 'Enter brand name' }]}>
                  <Input placeholder="Your Brand" />
                </Form.Item>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label="Primary Color" name="primaryColor">
                      <ColorPicker format="hex" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Secondary Color" name="secondaryColor">
                      <ColorPicker format="hex" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider style={{ margin: '8px 0' }} />
                <Space align="center" style={{ width: '100%' }}>
                  <Upload.Dragger
                    name="logo"
                    accept="image/*"
                    maxCount={1}
                    showUploadList={false}
                    customRequest={({ file, onSuccess }) => {
                      setTimeout(() => {
                        handleUpload({ file });
                        onSuccess && onSuccess('ok');
                      }, 400);
                    }}
                    style={{ flex: 1 }}
                  >
                    <p className="ant-upload-drag-icon"><UploadOutlined /></p>
                    <p className="ant-upload-text">Click or drag logo to upload</p>
                    <p className="ant-upload-hint">PNG or SVG recommended. Max 1MB.</p>
                  </Upload.Dragger>
                  {logoUrl && (
                    <div style={{ marginLeft: 12 }}>
                      <Text type="secondary">Preview</Text>
                      <div style={{ border: '1px solid #f0f0f0', padding: 8, borderRadius: 6, marginTop: 6 }}>
                        <img src={logoUrl} alt="Logo preview" style={{ maxWidth: 120, maxHeight: 60, objectFit: 'contain' }} />
                      </div>
                    </div>
                  )}
                </Space>
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card title="Domain & Emails" extra={<GlobalOutlined />}>
                <Form.Item label="Custom Domain" name="domain" rules={[{ required: true, message: 'Enter your white-label domain' }]}>
                  <Input placeholder="agency.yourdomain.com" addonBefore="https://" />
                </Form.Item>
                <Form.Item label="Email From Address" name="customEmailFrom" rules={[{ type: 'email', message: 'Enter a valid email' }]}>
                  <Input placeholder="noreply@yourdomain.com" prefix={<MailOutlined />} />
                </Form.Item>
                <Form.Item label="White-label Emails" name="whiteLabelEmails" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Space>
                  <Button onClick={() => message.info('DNS guide coming soon')}>DNS Setup Guide</Button>
                  <Button onClick={() => message.info('Verify domain request sent')}>Verify Domain</Button>
                </Space>
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Email Templates" extra={<MailOutlined />}>
                <Table
                  rowKey="key"
                  columns={columns}
                  dataSource={emailTemplates}
                  pagination={{ pageSize: 5 }}
                />
              </Card>
            </Col>
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Space>
              <Button onClick={() => form.resetFields()}>Reset</Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>Save Changes</Button>
            </Space>
          </div>
        </Form>
      </Space>
    </div>
  );
};

export default WhiteLabelBranding;
